import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { IMPORT_ACCEPT } from '../../apis';
import ReviewWindowRulePicker from './ReviewWindowRulePicker';
import type { ReviewWindowRule } from '../../types';

/** The templates a review starts from, until the server's list lands. */
export const FALLBACK_TEMPLATES = [
  { id: 'wbr', name: 'Weekly Business Review', description: 'KPI scorecard · trends · segments · commentary' },
  { id: 'experiment', name: 'Experiment Readout', description: 'A/B exposure, lift, and significance' },
  { id: 'kpi_summary', name: 'KPI Summary', description: 'one-page scorecard, comparisons built in' },
];

export type StartFrom = 'template' | 'deck' | 'report' | 'blank';

/**
 * THE one way a review starts (#317): a name, its grounding, and one choice
 * of where it starts from — a template, last period's deck (every number
 * re-derived before you trust it, #190/#191), or a live report (#192). The
 * samples are links inside those choices, not doors of their own. Rendered
 * in the "+ New review" dialog and, for a review tab that was never set up,
 * inline on that tab.
 *
 * `threadId` is the review to start; without one the form mints it on
 * submit (`newReview`) and starts that. `onDone` fires once the start was
 * handed to the store — the tab it opened shows what happens next.
 */
export default function ReviewSetupForm({ threadId, onDone, onCancel, onWorking, initialFrom }: { threadId?: string | null; onDone?: () => void; onCancel?: () => void; onWorking?: (working: boolean) => void; initialFrom?: StartFrom }) {
  // the reviews page (#568) adds a blank start and the window rule to this one setup
  const thread = useVoyagerStore((s) => (threadId ? s.savedThreads.find((t) => t.id === threadId) : undefined));
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const selectedPerspectiveId = useVoyagerStore((s) => s.selectedPerspectiveId);
  const templates = useVoyagerStore((s) => s.reviewTemplates);
  const fetchReviewTemplates = useVoyagerStore((s) => s.fetchReviewTemplates);
  const newReview = useVoyagerStore((s) => s.newReview);
  const loadThread = useVoyagerStore((s) => s.loadThread);
  const clearReconcile = useVoyagerStore((s) => s.clearReconcile);
  const renameThread = useVoyagerStore((s) => s.renameThread);
  const startReview = useVoyagerStore((s) => s.startReview);
  const reconcileSample = useVoyagerStore((s) => s.reconcileSample);
  const importArtifact = useVoyagerStore((s) => s.importArtifact);
  const connectReport = useVoyagerStore((s) => s.connectReport);
  const liveSamples = useVoyagerStore((s) => s.liveReportSamples);
  const fetchLiveReportSamples = useVoyagerStore((s) => s.fetchLiveReportSamples);
  const connections = useVoyagerStore((s) => s.connections);
  const fetchConnections = useVoyagerStore((s) => s.fetchConnections);
  const busy = useVoyagerStore((s) => (threadId ? s.reviewsBusy[threadId] : undefined));
  const error = useVoyagerStore((s) => s.reviewsError);

  const list = templates.length ? templates : FALLBACK_TEMPLATES;
  const [name, setName] = useState('');
  const [perspectiveId, setPerspectiveId] = useState<string>(() => { const id = thread?.perspectiveId || selectedPerspectiveId; return id && id !== 'auto' ? id : ''; });
  const [from, setFrom] = useState<StartFrom>(initialFrom || 'template');
  const [windowRule, setWindowRule] = useState<ReviewWindowRule | null>(null);
  const [templateId, setTemplateId] = useState<string>('wbr');
  const [reportId, setReportId] = useState<string>('');
  const [working, setWorkingState] = useState(false);
  const setWorking = (w: boolean) => { setWorkingState(w); onWorking?.(w); };
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (templates.length === 0) fetchReviewTemplates(); }, [templates.length, fetchReviewTemplates]);
  useEffect(() => { fetchLiveReportSamples(); if (connections.length === 0) fetchConnections(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!list.some((t) => t.id === templateId)) setTemplateId(list[0]?.id || 'wbr'); }, [list, templateId]);
  const reports = useMemo(() => connections.filter((c) => c.kind === 'report'), [connections]);
  const sample = liveSamples.find((s) => s.id === 'instadelivery-wbr');
  const template = list.find((t) => t.id === templateId);
  const disabled = working || !!busy;
  const noGrounding = !perspectiveId ? 'A review needs a fixed grounding — pick a perspective first' : '';

  /** The review this form starts: the tab it lives on, else one minted now — not opened until the start went through. */
  const target = async (): Promise<string | null> => {
    if (threadId) return threadId;
    return newReview({ open: false });
  };
  /**
   * A name typed here is the review's name; nothing typed leaves the template's or the deck's. It is on the
   * server BEFORE the start or the grading — a start keeps any name that is not the default, and the grading
   * screen's Build must never race a rename still in flight.
   */
  const named = async (id: string): Promise<boolean> => {
    const next = name.trim();
    const current = useVoyagerStore.getState().savedThreads.find((t) => t.id === id);
    if (!next || !current || next === current.title) return true;
    const ok = await renameThread(id, next);
    if (!ok) useVoyagerStore.setState({ reviewsError: `Could not name the review “${next}” — try again` } as any);
    return ok;
  };
  /**
   * Start the review. A start that fails leaves the form as it was, its
   * choices and the error in place; one that went through names the review,
   * opens its tab and closes the dialog.
   */
  const run = async (act: (id: string) => Promise<boolean | null | undefined> | void) => {
    setWorking(true);
    try {
      const id = await target();
      if (!id) return;
      // the name did not take: nothing starts nameless — the form stays, the name and the error with it
      if (!(await named(id))) return;
      // a grading left unbuilt on this tab is not this start's: it goes before the review starts
      if (useVoyagerStore.getState().reconcile[id]) clearReconcile(id);
      const ok = await act(id);
      if (ok === false) {
        // a deck or report that was read but could not be graded: the grading screen holds the error and its retry
        if (useVoyagerStore.getState().reconcile[id]) { if (!threadId) loadThread(id); onDone?.(); }
        return;
      }
      if (!threadId) loadThread(id);
      onDone?.();
    } finally {
      setWorking(false);
    }
  };
  const start = () => {
    // a rule chosen here is the review's from its start (#408); none keeps today's behaviour — and today's call
    if (from === 'template') return run((id) => (windowRule ? startReview(id, templateId, perspectiveId, undefined, null, windowRule) : startReview(id, templateId, perspectiveId)));
    if (from === 'blank') return run((id) => startReview(id, 'blank', perspectiveId, undefined, null, windowRule || undefined));
    if (from === 'report') return run((id) => connectReport(id, { connectionId: reportId }, perspectiveId));
    fileInput.current?.click();
    return undefined;
  };
  const primaryLabel = busy === 'creating' ? 'Saving…' : busy === 'starting' ? 'Building…' : busy === 'reconciling' ? (from === 'report' ? 'Reading…' : 'Reconciling…')
    : from === 'template' || from === 'blank' ? 'Start' : from === 'deck' ? 'Upload a deck' : 'Connect';
  const primaryDisabled = disabled || !perspectiveId || (from === 'report' && !reportId)
    || (windowRule?.kind === 'trailing_days' && (!Number.isSafeInteger(windowRule.days) || windowRule.days < 1));
  const primaryHint = noGrounding || (from === 'report' && reports.length === 0 ? 'Add a Tableau connection under Connections first' : '');

  return (
    <Box data-testid="review-setup" sx={{ display: 'grid', gap: 2 }}>
      <input
        ref={fileInput}
        type="file"
        accept={IMPORT_ACCEPT}
        style={{ display: 'none' }}
        data-testid="review-upload-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && perspectiveId) run((id) => importArtifact(id, file, perspectiveId));
        }}
      />
      <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value.slice(0, 200))} placeholder={from === 'template' ? template?.name || '' : 'Weekly Business Review — the template a deck or report builds'}
        inputProps={{ 'data-testid': 'review-name', 'aria-label': 'Review name' }} />
      <FormControl size="small">
        <InputLabel id="review-perspective-label">Grounded on</InputLabel>
        <Select labelId="review-perspective-label" label="Grounded on" value={perspectiveId} onChange={(e) => setPerspectiveId(String(e.target.value))} inputProps={{ 'data-testid': 'review-perspective' }}>
          {perspectives.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          {perspectiveId && !perspectives.some((p) => p.id === perspectiveId) && <MenuItem value={perspectiveId}>{perspectiveId}</MenuItem>}
        </Select>
      </FormControl>
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>Start from</Typography>
        <RadioGroup value={from} onChange={(e) => setFrom(e.target.value as StartFrom)} sx={{ gap: 0.5, mt: 0.5 }}>
          <Box data-testid="review-path-template">
            <FormControlLabel value="template" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>A template</Typography>} />
            {from === 'template' && (
              <Box sx={{ pl: 3.5, display: 'grid', gap: 0.5 }}>
                <Select size="small" value={templateId} onChange={(e) => setTemplateId(String(e.target.value))} inputProps={{ 'data-testid': 'review-template' }} sx={{ maxWidth: 320 }}>
                  {list.map((t) => <MenuItem key={t.id} value={t.id} data-testid={`review-template-${t.id}`}>{t.name}</MenuItem>)}
                </Select>
                {template?.description && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{template.description}</Typography>}
              </Box>
            )}
          </Box>
          <Box data-testid="review-path-deck">
            <FormControlLabel value="deck" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Last period's deck</Typography>} />
            {from === 'deck' && (
              <Box sx={{ pl: 3.5, display: 'grid', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>PowerPoint (.pptx), PDF or Excel (.xlsx) · every number it printed is re-derived from the warehouse before you trust it</Typography>
                <Link component="button" type="button" variant="caption" underline="hover" data-testid="review-sample-deck" disabled={disabled}
                  onClick={() => run((id) => reconcileSample(id, 'instadelivery-wbr'))} sx={{ justifySelf: 'start', color: 'primary.dark', '&:disabled': { color: 'text.disabled', cursor: 'default' } }}>
                  or try the sample — Instadelivery WBR (demo data)
                </Link>
              </Box>
            )}
          </Box>
          <Box data-testid="review-path-report">
            <FormControlLabel value="report" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>A live report</Typography>} />
            {from === 'report' && (
              <Box sx={{ pl: 3.5, display: 'grid', gap: 0.75 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Tableau — reads the scorecard sheet's tiles with the formula behind each, then refreshes the review when the report does</Typography>
                {reports.length > 0 && (
                  <FormControl size="small" sx={{ maxWidth: 320 }}>
                    <InputLabel id="review-report-label">Report</InputLabel>
                    <Select labelId="review-report-label" label="Report" value={reportId} onChange={(e) => setReportId(String(e.target.value))} inputProps={{ 'data-testid': 'review-report' }}>
                      {reports.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                )}
                <Tooltip title={sample && !sample.available ? 'The sample report is not configured on this install' : ''} placement="top">
                  <span style={{ justifySelf: 'start' }}>
                    <Link component="button" type="button" variant="caption" underline="hover" data-testid="review-sample-report" disabled={disabled || !sample?.available}
                      onClick={() => run((id) => connectReport(id, { sampleId: 'instadelivery-wbr' }))} sx={{ color: 'primary.dark', '&:disabled': { color: 'text.disabled', cursor: 'default' } }}>
                      or try the sample — Instadelivery WBR on Tableau (demo data)
                    </Link>
                  </span>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Box data-testid="review-path-blank">
            <FormControlLabel value="blank" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Blank</Typography>} />
            {from === 'blank' && (
              <Typography variant="caption" sx={{ display: 'block', pl: 3.5, color: 'text.secondary' }}>No template sections — the numbers you pin are the review</Typography>
            )}
          </Box>
        </RadioGroup>
      </Box>
      {(from === 'template' || from === 'blank') && (
        <Box data-testid="review-setup-window-rule" sx={{ display: 'grid', gap: 0.5 }}>
          <ReviewWindowRulePicker value={windowRule} onChange={setWindowRule} allowLegacy disabled={disabled} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>A deck or a report asks for its rule once its numbers are graded; you can change it on the review afterwards.</Typography>
        </Box>
      )}
      {error && <Alert severity="error" data-testid="review-error" onClose={() => useVoyagerStore.setState({ reviewsError: null } as any)}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
        {onCancel && <Button size="small" onClick={onCancel} disabled={working}>Cancel</Button>}
        <Tooltip title={primaryHint} placement="top">
          <span>
            <Button size="small" variant="contained" disabled={primaryDisabled} onClick={start} data-testid="review-start">{primaryLabel}</Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
